import React, { useState, useMemo, useEffect, useRef, useCallback, Fragment } from 'react';
import Fuse from 'fuse.js';
import { getFaqSectionIndexData } from '../api/faqSection';
import { useAnswerCache } from '../Hooks/useAnswerCache';
import { translate } from '../Services/translation';

const Loader = () => (
    <div className="flex items-center gap-2 text-accent text-sm font-semibold py-4">
        <span>{translate('general.loading')}</span>
        <span className="icon icon-outlined !text-accent !text-sm animate-hourglass-flip" aria-hidden="true">
            hourglass
        </span>
    </div>
);

const getPath = (u) => {
    try {
        return new URL(u, typeof window !== 'undefined' ? window.location.origin : undefined).pathname;
    } catch {
        return u;
    }
};

const normalizeQuery = (value = '') =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/-/g, '')
        .trim();

const getSearchValue = (item) => item.titleNorm || normalizeQuery(item.title);

const FaqSection = ({ data: { culture, title, text, popularQuestions } }) => {
    const [data, setData] = useState({
        categories: [],
        questions: {
            byId: {},
            allIds: [],
            byCategory: {}
        },
        popularQuestionIds: []
    });

    const [searchInput, setSearchInput] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('');
    const [expandedQuestion, setExpandedQuestion] = useState(null);

    const inputRef = useRef(null);
    const questionRefs = useRef({});
    const hasScrolledRef = useRef(false);
    const { fetchAnswer, getAnswer, hasAnswer, isLoading } = useAnswerCache();

    const didInitFromUrl = useRef(false);
    const initialHref = useRef(typeof window !== 'undefined' ? window.location.href : '');

    const handleQuestionClick = async (questionId) => {
        if (expandedQuestion === questionId) {
            setExpandedQuestion(null);
            return;
        }

        await fetchAnswer(questionId, culture);
        setExpandedQuestion(questionId);

        setTimeout(() => {
            const element = questionRefs.current[questionId];
            if (!element) return;

            const elementRect = element.getBoundingClientRect();
            const isInView =
                elementRect.top >= 0 &&
                elementRect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

            if (!isInView) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }, 0);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setActiveSearch('');
        setActiveCategory('');
        setExpandedQuestion(null);
        inputRef.current?.focus();
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setActiveCategory('');
        setExpandedQuestion(null);
        setActiveSearch(searchInput);
    };

    const handleCategoryClick = (categoryId) => {
        setActiveCategory(categoryId);
        setExpandedQuestion(null);
    };

    const handleCategoryChange = (e) => {
        setActiveCategory(e.target.value);
        setExpandedQuestion(null);
    };

    const getCategoryTitle = (categoryId) => {
        return data.categories.find((cat) => cat.id === categoryId)?.title;
    };

    useEffect(() => {
        getFaqSectionIndexData(culture).then((apiData) => {
            const normalized = {
                categories: apiData.categories || [],
                questions: {
                    byId: {},
                    allIds: [],
                    byCategory: {}
                },
                popularQuestionIds: apiData.popularQuestionIds || []
            };

            (apiData.questions || []).forEach((q) => {
                normalized.questions.byId[q.id] = q;
                normalized.questions.allIds.push(q.id);

                if (!normalized.questions.byCategory[q.categoryId]) {
                    normalized.questions.byCategory[q.categoryId] = [];
                }

                normalized.questions.byCategory[q.categoryId].push(q.id);
            });

            setData(normalized);
        });
    }, [culture]);

    const CategoryButton = useCallback(
        ({ category, isActive, onClick }) => (
            <button
                key={category.id}
                type="button"
                className={`text-xl leading-[1.5] text-accent p-[0.625rem] w-full text-left hover:bg-tertiary transition-colors rounded-2xl ${isActive ? 'rounded-2xl bg-tertiary font-bold' : 'font-medium'
                    }`}
                onClick={() => onClick(category.id)}
            >
                {category.title}
            </button>
        ),
        []
    );

    const QuestionItem = useCallback(
        ({ question, expanded, onToggle }) => (
            <div
                key={question.id}
                ref={(el) => {
                    questionRefs.current[question.id] = el;
                }}
                className="scroll-mt-header"
            >
                <button
                    type="button"
                    onClick={() => onToggle(question.id)}
                    className={`w-full text-xl text-left leading-[1.5] text-accent py-3 cursor-pointer flex items-center justify-between gap-2 ${expanded ? 'font-extrabold' : 'font-medium'
                        }`}
                    aria-describedby={`answer-${question.id}`}
                    aria-expanded={expanded}
                >
                    <span>{question.title}</span>
                    <span
                        className={`icon icon-outline text-current transition-transform duration-200 ${expanded ? 'rotate-180' : ''
                            }`}
                        aria-hidden="true"
                    >
                        keyboard_arrow_down
                    </span>
                </button>

                {isLoading(question.id) && <Loader />}

                {expanded && hasAnswer(question.id) && (
                    <div
                        id={`answer-${question.id}`}
                        className="p-4 lg:p-8 rounded-radius border border-border-primary bg-grey-lighter mb-4 text-text-secondary space-y-4 [&_iframe]:w-full"
                        dangerouslySetInnerHTML={{ __html: getAnswer(question.id) }}
                    />
                )}
            </div>
        ),
        [getAnswer, hasAnswer, isLoading]
    );

    const filteredQuestions = useMemo(() => {
        const allQuestions = data.questions.allIds
            .map((id) => data.questions.byId[id])
            .filter(Boolean);

        if (!activeSearch.trim()) {
            return allQuestions;
        }

        const normalizedTerm = normalizeQuery(activeSearch);

        if (!normalizedTerm) {
            return allQuestions;
        }

        const questionFuse = new Fuse(allQuestions, {
            includeScore: true,
            threshold: 0.4,
            ignoreLocation: true,
            keys: [
                {
                    name: 'titleNorm',
                    weight: 1,
                    getFn: getSearchValue
                }
            ]
        });

        const categoryFuse = new Fuse(data.categories, {
            includeScore: true,
            threshold: 0.4,
            ignoreLocation: true,
            keys: [
                {
                    name: 'titleNorm',
                    weight: 1,
                    getFn: getSearchValue
                }
            ]
        });

        const matchingQuestionIds = new Set();

        questionFuse.search(normalizedTerm).forEach((result) => {
            if (result.score < 0.6) {
                matchingQuestionIds.add(result.item.id);
            }
        });

        categoryFuse.search(normalizedTerm).forEach((result) => {
            if (result.score < 0.6) {
                const categoryId = result.item.id;

                if (data.questions.byCategory[categoryId]) {
                    data.questions.byCategory[categoryId].forEach((id) => {
                        matchingQuestionIds.add(id);
                    });
                }
            }
        });

        return Array.from(matchingQuestionIds)
            .map((id) => data.questions.byId[id])
            .filter(Boolean);
    }, [data.questions, data.categories, activeSearch]);

    const filteredCategories = useMemo(() => {
        if (!activeSearch.trim()) {
            return data.categories;
        }

        const categoryIds = new Set(filteredQuestions.map((q) => q.categoryId));
        return data.categories.filter((cat) => categoryIds.has(cat.id));
    }, [data.categories, filteredQuestions, activeSearch]);

    useEffect(() => {
        if (!activeSearch.trim()) return;
        if (filteredCategories.length === 0) return;

        setActiveCategory((prev) => {
            if (prev && filteredCategories.some((c) => c.id === prev)) {
                return prev;
            }

            return filteredCategories[0].id;
        });
    }, [activeSearch, filteredCategories]);

    useEffect(() => {
        if (!hasScrolledRef.current && expandedQuestion && questionRefs.current[expandedQuestion]) {
            const timeout = setTimeout(() => {
                questionRefs.current[expandedQuestion].scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
                hasScrolledRef.current = true;
            }, 100);

            return () => clearTimeout(timeout);
        }
    }, [expandedQuestion]);

    useEffect(() => {
        if (didInitFromUrl.current) return;

        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (!path) return;

        if (!data.questions.allIds.length || !data.categories.length) return;

        const qId = data.questions.allIds.find((id) => getPath(data.questions.byId[id]?.url) === path);

        if (qId) {
            const q = data.questions.byId[qId];

            setActiveCategory(q.categoryId);
            setExpandedQuestion(qId);

            if (!hasAnswer(qId)) {
                fetchAnswer(qId, culture);
            }

            didInitFromUrl.current = true;
            return;
        }

        const cat = data.categories.find((c) => getPath(c.url) === path);

        if (cat) {
            setActiveCategory(cat.id);
            setExpandedQuestion(null);
            didInitFromUrl.current = true;
            return;
        }

        didInitFromUrl.current = true;
    }, [data.categories, data.questions, hasAnswer, fetchAnswer, culture]);

    useEffect(() => {
        if (!didInitFromUrl.current) return;

        let targetUrl = null;

        if (expandedQuestion) {
            targetUrl = data.questions.byId[expandedQuestion]?.url || null;
        } else if (activeCategory) {
            const cat = data.categories.find((c) => c.id === activeCategory);
            targetUrl = cat?.url || null;
        } else {
            targetUrl = initialHref.current || null;
        }

        if (typeof window !== 'undefined' && targetUrl) {
            const targetPath = getPath(targetUrl);

            if (window.location.pathname !== targetPath) {
                try {
                    window.history.pushState(null, '', targetPath);
                } catch (e) {
                    console.error(e);
                }
            }
        }
    }, [activeCategory, expandedQuestion, data.categories, data.questions]);

    return (
        <Fragment>
            <div className="border border-border-primary bg-white rounded-2xl p-4 lg:px-8 lg:py-10 space-y-4 lg:space-y-10">
                <h1 className="text-[2rem] leading-[2.5rem] text-text-secondary font-extrabold">{title}</h1>

                {text && (
                    <div
                        className="text-base text-text-secondary max-w-[85ch] prose"
                        dangerouslySetInnerHTML={{ __html: text }}
                    />
                )}

                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2 items-center">
                    <div className="relative bg-input-primary rounded-radius h-12 flex items-center gap-2 w-full md:max-w-[26.25rem] overflow-hidden px-6 py-2 border border-input-secondary leading-[1]">
                        <span className="icon icon-outlined !text-input-secondary" aria-hidden="true">
                            search
                        </span>

                        <input
                            className="w-full text-base leading-5 outline-none placeholder:text-input-secondary text-text-secondary"
                            type="text"
                            autoComplete="off"
                            placeholder={translate('faq.search.placeholder')}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            ref={inputRef}
                        />

                        {searchInput ? (
                            <button type="button" className="flex" aria-label="Clear search" onClick={handleClearSearch}>
                                <span className="icon icon-outlined !text-text-secondary" aria-hidden="true">
                                    close
                                </span>
                            </button>
                        ) : (
                            <div className="w-6" />
                        )}
                    </div>

                    <button type="submit" className="button w-full md:w-auto md:min-w-[7.5rem] text-sm">
                        {translate('general.search')}
                    </button>
                </form>
            </div>

            {activeSearch && filteredQuestions.length === 0 && (
                <div className="border border-border-primary bg-white p-4 lg:p-6 rounded-2xl space-y-4 lg:space-y-6 grid place-items-center">
                    <p className="text-base font-semibold">
                        {translate('faq.searchresult.nohits.for')} "{activeSearch}"
                    </p>
                </div>
            )}

            {activeSearch && filteredQuestions.length > 0 && (
                <div className="border border-border-primary bg-white p-4 lg:py-10 lg:px-8 rounded-2xl grid lg:grid-cols-[1fr_3fr] gap-4 lg:gap-10">
                    <div className="hidden md:flex flex-col">
                        {filteredCategories.map((category) => (
                            <CategoryButton
                                key={category.id}
                                category={category}
                                isActive={activeCategory === category.id}
                                onClick={handleCategoryClick}
                            />
                        ))}
                    </div>

                    <select
                        className="w-full border border-border-primary rounded-radius form__select md:hidden"
                        value={activeCategory}
                        onChange={handleCategoryChange}
                    >
                        {filteredCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {getCategoryTitle(category.id)}
                            </option>
                        ))}
                    </select>

                    <div className="space-y-6">
                        <div className="border-b border-border-primary pb-6">
                            <h4 className="text-[2rem] leading-[2.5rem] font-bold text-accent">
                                {getCategoryTitle(activeCategory)}
                            </h4>
                        </div>

                        <div>
                            {filteredQuestions
                                .filter((q) => q.categoryId === activeCategory)
                                .map((question) => (
                                    <QuestionItem
                                        key={question.id}
                                        question={question}
                                        expanded={expandedQuestion === question.id}
                                        onToggle={handleQuestionClick}
                                    />
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {!activeSearch && (
                <div className="border border-border-primary bg-white p-4 lg:py-10 lg:px-8 rounded-2xl space-y-4 lg:space-y-10 grid lg:grid-cols-[1fr_3fr] gap-4 lg:gap-10">
                    <div className="hidden md:flex flex-col">
                        {filteredCategories.map((category) => (
                            <CategoryButton
                                key={category.id}
                                category={category}
                                isActive={activeCategory === category.id}
                                onClick={handleCategoryClick}
                            />
                        ))}
                    </div>

                    <select
                        className="w-full border border-border-primary rounded-radius form__select md:hidden"
                        value={activeCategory}
                        onChange={handleCategoryChange}
                    >
                        <option value="">{translate('faq.select_category')}</option>

                        {filteredCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {getCategoryTitle(category.id)}
                            </option>
                        ))}
                    </select>

                    <div className="space-y-6">
                        <div className="border-b border-border-primary pb-6">
                            <h4 className="text-[2rem] leading-[2.5rem] font-bold text-accent">
                                {activeCategory ? getCategoryTitle(activeCategory) : popularQuestions}
                            </h4>
                        </div>

                        <div>
                            {activeCategory
                                ? (data.questions.byCategory[activeCategory] || [])
                                    .map((questionId) => data.questions.byId[questionId])
                                    .filter(Boolean)
                                    .map((question) => (
                                        <QuestionItem
                                            key={question.id}
                                            question={question}
                                            expanded={expandedQuestion === question.id}
                                            onToggle={handleQuestionClick}
                                        />
                                    ))
                                : data.popularQuestionIds
                                    .map((questionId) => data.questions.byId[questionId])
                                    .filter(Boolean)
                                    .map((question) => (
                                        <QuestionItem
                                            key={question.id}
                                            question={question}
                                            expanded={expandedQuestion === question.id}
                                            onToggle={handleQuestionClick}
                                        />
                                    ))}
                        </div>
                    </div>
                </div>
            )}

            <div />
        </Fragment>
    );
};

export default FaqSection;